import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Gauge, ArrowLeft, Home as HomeIcon, Wallet, Leaf, Recycle, Search, ClipboardCheck, Wrench, HardDrive, Copy, CheckCircle2, Loader2, AlertCircle, Send, Info, Laptop, Apple } from "lucide-react";
import { Seo, serviceJsonLd } from "../components/Seo";

const INCLUDED = [
  { Icon: Copy, t: "Kloning & överföring av data", d: "All din befintliga data flyttas över – filer, bilder, program och inställningar följer med." },
  { Icon: HardDrive, t: "Installation av ny snabb SSD", d: "Vi byter till eller lägger till en snabb SSD/hårddisk för mer fart och lagringsutrymme." },
  { Icon: ClipboardCheck, t: "Funktionstest", d: "Vi testar att allt fungerar som det ska innan du får tillbaka datorn." },
  { Icon: Gauge, t: "Personlig genomgång", d: "Vi visar skillnaden och ger dig tips så att datorn håller sig snabb längre." },
];

const STEPS = [
  { Icon: Send, t: "1. Boka eller hör av dig", d: "Fyll i formuläret med din datormodell så tar vi kontakt." },
  { Icon: Search, t: "2. Analys via modellnummer", d: "Vi kontrollerar exakt vad just din dator går att uppgradera med." },
  { Icon: Wrench, t: "3. Fysisk uppgradering", d: "Vi installerar den nya disken och flyttar över all din data." },
  { Icon: CheckCircle2, t: "4. Klar & testad", d: "Du får tillbaka en snabbare dator med mer utrymme – redo att användas." },
];

export default function DiskUpgrade() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", computer_model: "", message: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.computer_model.trim()) {
      setError("Fyll i namn, telefonnummer och din datormodell.");
      return;
    }
    if (!validEmail) { setError("Skriv in en giltig e-postadress."); return; }
    setSubmitting(true);
    try {
      await api.post("/upgrade", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Något gick fel. Försök igen om en stund.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title="Snabbare dator & mer lagring"
        description="Slipp köpa ny dator – vi uppgraderar din nuvarande med snabb SSD och mer lagring. Spara pengar, bättre för miljön och förlängd livslängd. Vi kollar vad just din modell klarar."
        path="/uppgradering"
        jsonLd={serviceJsonLd({ name: "Uppgradering av dator – snabbare & mer lagring", description: "Fysisk uppgradering av dator med SSD och mer lagringsutrymme.", path: "/uppgradering" })}
      />
      <Link to="/" data-testid="upgrade-back" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka till start
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6 flex items-start gap-3">
        <Gauge size={46} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-1" /> En dator som ny – till en bråkdel av priset
      </h1>
      <h2 className="font-heading text-lg md:text-xl font-semibold text-[#4A4A4A] mt-4 leading-relaxed">
        Vi uppgraderar din nuvarande dator med en snabb SSD och mer lagring, så att den känns pigg igen – utan att du behöver köpa nytt.
      </h2>

      {/* Value proposition */}
      <div className="bg-blue-50 border-2 border-blue-100 rounded-xl p-6 md:p-8 mt-8">
        <p className="text-lg md:text-xl leading-relaxed text-[#1A1A1A]">
          Har du en långsam dator eller fått slut på lagringsutrymme? Innan du lägger tusenlappar på en
          helt ny dator – låt oss ge din nuvarande ett nytt liv. Ofta räcker en ny disk för att datorn
          ska starta snabbare, ladda program direkt och rymma alla dina bilder igen.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <div className="flex items-start gap-3"><Wallet size={26} strokeWidth={2} className="text-[#0056D2] shrink-0 mt-0.5" /><span className="text-base text-[#1A1A1A]"><strong>Spara pengar.</strong> En bråkdel av priset mot en ny dator.</span></div>
          <div className="flex items-start gap-3"><Leaf size={26} strokeWidth={2} className="text-[#0056D2] shrink-0 mt-0.5" /><span className="text-base text-[#1A1A1A]"><strong>Bättre för miljön.</strong> Mindre elektronikskrot.</span></div>
          <div className="flex items-start gap-3"><Recycle size={26} strokeWidth={2} className="text-[#0056D2] shrink-0 mt-0.5" /><span className="text-base text-[#1A1A1A]"><strong>Förlängd livslängd.</strong> Använd det du redan äger längre.</span></div>
        </div>
      </div>

      {/* Compatibility / model number */}
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] mt-10">Så kollar vi vad som passar din dator</h2>
      <p className="text-lg text-[#4A4A4A] mt-3 leading-relaxed">
        Alla datorer går inte att uppgradera på samma sätt – vad som är möjligt beror helt på din modell.
        Därför behöver vi ditt <strong>modellnummer</strong>. Så här hittar du det:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
          <h3 className="font-heading text-xl font-bold text-[#1A1A1A] flex items-center gap-2"><Laptop size={24} strokeWidth={2.5} className="text-[#0056D2]" /> Windows-dator</h3>
          <ul className="mt-3 space-y-2 text-base text-[#1A1A1A]">
            <li className="flex gap-2"><span className="font-bold text-[#0056D2]">1.</span> Skriv <em>Systeminformation</em> i sökrutan vid startknappen och öppna den.</li>
            <li className="flex gap-2"><span className="font-bold text-[#0056D2]">2.</span> Läs av raden <em>Systemmodell</em>.</li>
            <li className="flex gap-2"><span className="font-bold text-[#0056D2]">3.</span> Alternativt: vänd på datorn – modellnumret står oftast på en etikett på undersidan.</li>
          </ul>
        </div>
        <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
          <h3 className="font-heading text-xl font-bold text-[#1A1A1A] flex items-center gap-2"><Apple size={24} strokeWidth={2.5} className="text-[#0056D2]" /> Mac / MacBook</h3>
          <ul className="mt-3 space-y-2 text-base text-[#1A1A1A]">
            <li className="flex gap-2"><span className="font-bold text-[#0056D2]">1.</span> Klicka på Apple-symbolen <strong></strong> uppe till vänster.</li>
            <li className="flex gap-2"><span className="font-bold text-[#0056D2]">2.</span> Välj <em>Om den här datorn</em> – där står modell och årsmodell.</li>
            <li className="flex gap-2"><span className="font-bold text-[#0056D2]">3.</span> Alternativt: läs den lilla texten på datorns undersida.</li>
          </ul>
        </div>
      </div>
      <p className="text-base text-[#4A4A4A] mt-4 flex items-start gap-2 bg-gray-50 border-2 border-gray-100 rounded-lg p-4">
        <Info size={22} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-0.5" />
        Osäker? Ingen fara – skriv bara det du hittar (märke, modell eller årtal) i formuläret nedan, så hjälper vi dig att ta reda på resten.
      </p>

      {/* How it works */}
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] mt-10">Så här fungerar det</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
        {STEPS.map(({ Icon, t, d }) => (
          <div key={t} className="bg-white border-2 border-gray-300 rounded-xl p-6 flex flex-col items-start">
            <span className="bg-blue-50 text-[#0056D2] rounded-xl p-3"><Icon size={28} strokeWidth={2} /></span>
            <h3 className="font-heading text-xl font-bold text-[#1A1A1A] mt-4">{t}</h3>
            <p className="text-base text-[#4A4A4A] mt-2">{d}</p>
          </div>
        ))}
      </div>

      {/* What's included */}
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] mt-10">Detta ingår</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
        {INCLUDED.map(({ Icon, t, d }) => (
          <div key={t} className="bg-white border-2 border-gray-300 rounded-xl p-6 flex flex-col items-start">
            <span className="bg-blue-50 text-[#0056D2] rounded-xl p-3"><Icon size={28} strokeWidth={2} /></span>
            <h3 className="font-heading text-xl font-bold text-[#1A1A1A] mt-4">{t}</h3>
            <p className="text-base text-[#4A4A4A] mt-2">{d}</p>
          </div>
        ))}
      </div>

      {/* Form */}
      {done ? (
        <div data-testid="upgrade-success" className="bg-white border-2 border-gray-300 rounded-xl p-8 mt-10 text-center">
          <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4">
            <CheckCircle2 size={56} strokeWidth={2} />
          </span>
          <h2 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-5">Tack för din förfrågan!</h2>
          <p className="text-lg text-[#4A4A4A] mt-3">
            Jag har tagit emot dina uppgifter och återkommer så snart som möjligt med vad som passar just din dator.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-6">
            <HomeIcon size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A]">Få reda på vad din dator kan uppgraderas med</h2>
          <p className="text-base text-[#4A4A4A] mt-2">Fyll i dina uppgifter och din datormodell så återkommer jag med ett förslag. Det tar bara en minut.</p>

          <div className="space-y-6 mt-6">
            <div>
              <label htmlFor="u-name" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Namn</label>
              <input id="u-name" data-testid="upgrade-name" value={form.name} onChange={set("name")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="För- och efternamn" />
            </div>
            <div>
              <label htmlFor="u-email" className="text-xl font-bold mb-2 block text-[#1A1A1A]">E-post</label>
              <input id="u-email" type="email" data-testid="upgrade-email" value={form.email} onChange={set("email")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="namn@exempel.se" />
            </div>
            <div>
              <label htmlFor="u-phone" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Telefonnummer</label>
              <input id="u-phone" type="tel" data-testid="upgrade-phone" value={form.phone} onChange={set("phone")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="07X-XXX XX XX" />
            </div>
            <div>
              <label htmlFor="u-model" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Datormodell &amp; modellnummer</label>
              <input id="u-model" data-testid="upgrade-model" value={form.computer_model} onChange={set("computer_model")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder='t.ex. HP Pavilion 15 / MacBook Air 2017 / vet ej' />
            </div>
            <div>
              <label htmlFor="u-message" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Meddelande</label>
              <textarea id="u-message" data-testid="upgrade-message" value={form.message} onChange={set("message")} rows={5}
                className="text-lg px-4 py-3 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2] resize-y"
                placeholder='Berätta gärna vad som är segt eller hur mycket mer utrymme du vill ha (valfritt)' />
            </div>

            {error && (
              <p data-testid="upgrade-error" className="flex items-center gap-2 text-[#D32F2F] text-lg font-semibold">
                <AlertCircle size={22} /> {error}
              </p>
            )}

            <button data-testid="upgrade-submit" type="submit" disabled={submitting}
              className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60">
              {submitting ? <Loader2 className="animate-spin" size={26} /> : <Send size={26} strokeWidth={2.5} />}
              {submitting ? "Skickar…" : "Skicka förfrågan"}
            </button>

            <p data-testid="gdpr-notice" className="text-sm text-[#4A4A4A] leading-relaxed text-center">
              Din integritet är viktig för oss. Vi delar aldrig dina uppgifter med tredje part och hanterar all data säkert i enlighet med GDPR.
            </p>
          </div>
        </form>
      )}
    </div>
  );
}
